// src/pages/AdminDashboard.js
import React, { useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { MOCK_VIDEOS, MOCK_STUDENTS, CATEGORIES } from "../data/mockData";

export default function AdminDashboard() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="videos" element={<VideoManager />} />
      <Route path="students" element={<StudentList />} />
    </Routes>
  );
}

function Overview() {
  const stats = [
    { label: "Total Videos", value: MOCK_VIDEOS.length, icon: "🎬", color: "var(--sp-yellow)" },
    { label: "Students", value: MOCK_STUDENTS.length, icon: "👥", color: "#60A5FA" },
    { label: "Active", value: MOCK_STUDENTS.filter(s => s.status === "active").length, icon: "✅", color: "#34D399" },
    { label: "Suspended", value: MOCK_STUDENTS.filter(s => s.status === "suspended").length, icon: "🚫", color: "#F87171" }
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--sp-surface)", fontFamily: "var(--font-body)" }}>
      {/* Admin header */}
      <div style={{ background: "var(--sp-black)", padding: "40px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{
              background: "var(--sp-yellow)", color: "var(--sp-black)",
              fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 99,
              letterSpacing: "0.08em"
            }}>ADMIN</div>
            <span style={{ color: "var(--sp-gray-mid)", fontSize: 13 }}>Control Panel</span>
          </div>
          <h1 style={{
            fontFamily: "var(--font-display)", fontSize: 48,
            color: "var(--sp-white)", letterSpacing: "0.04em"
          }}>
            ADMIN DASHBOARD
          </h1>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 40 }}>
          {stats.map((s, i) => (
            <div key={i} className={`animate-fade-in stagger-${i + 1}`} style={{
              background: "var(--sp-white)",
              border: "1px solid var(--sp-border)",
              borderRadius: "var(--radius-lg)",
              padding: "20px 24px",
              boxShadow: "var(--shadow-sm)"
            }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 40, color: s.color, letterSpacing: "0.02em" }}>
                {s.value}
              </div>
              <div style={{ fontSize: 13, color: "var(--sp-gray-mid)", fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <AdminCard title="Manage Videos" desc="Upload new lectures, edit metadata, or delete old content." to="/admin/videos" cta="Open Video Manager" />
          <AdminCard title="Manage Students" desc="View student accounts, check enrollment status, suspend or reactivate." to="/admin/students" cta="Open Student List" />
        </div>
      </div>
    </div>
  );
}

function AdminCard({ title, desc, to, cta }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      className="animate-fade-in"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "var(--sp-white)",
        border: hov ? "1px solid rgba(255,214,0,0.4)" : "1px solid var(--sp-border)",
        borderRadius: "var(--radius-lg)", padding: "28px 32px",
        transition: "all 0.2s ease",
        transform: hov ? "translateY(-2px)" : "none",
        boxShadow: hov ? "var(--shadow-md)" : "var(--shadow-sm)"
      }}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{title}</h3>
      <p style={{ fontSize: 14, color: "var(--sp-gray-mid)", marginBottom: 20, lineHeight: 1.6 }}>{desc}</p>
      <Link to={to} style={{
        display: "inline-block", padding: "10px 20px",
        background: "var(--sp-black)", color: "var(--sp-yellow)",
        borderRadius: 8, fontSize: 13, fontWeight: 700, letterSpacing: "0.02em"
      }}>{cta} →</Link>
    </div>
  );
}

function VideoManager() {
  const [videos, setVideos] = useState(MOCK_VIDEOS);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Physics", youtubeId: "", instructor: "" });
  const [deleteId, setDeleteId] = useState(null);

  function handleAdd(e) {
    e.preventDefault();
    const newVideo = {
      id: "v" + Date.now(),
      ...form,
      thumbnail: `https://img.youtube.com/vi/${form.youtubeId}/hqdefault.jpg`,
      duration: "—",
      views: 0,
      uploadedAt: new Date().toISOString().slice(0, 10),
      featured: false
    };
    setVideos(prev => [newVideo, ...prev]);
    setForm({ title: "", description: "", category: "Physics", youtubeId: "", instructor: "" });
    setShowForm(false);
  }

  function handleDelete(id) {
    setVideos(prev => prev.filter(v => v.id !== id));
    setDeleteId(null);
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--sp-surface)", padding: "32px 24px", fontFamily: "var(--font-body)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <div>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 36, letterSpacing: "0.04em" }}>VIDEO LIBRARY</h1>
            <p style={{ color: "var(--sp-gray-mid)", marginTop: 2 }}>{videos.length} videos</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: "11px 22px", background: "var(--sp-yellow)",
              color: "var(--sp-black)", fontWeight: 700, fontSize: 14,
              border: "none", borderRadius: "var(--radius-md)",
              cursor: "pointer", fontFamily: "var(--font-body)",
              transition: "all var(--transition)"
            }}
            onMouseEnter={e => { e.target.style.background = "var(--sp-yellow-dark)"; e.target.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.target.style.background = "var(--sp-yellow)"; e.target.style.transform = "none"; }}
          >
            {showForm ? "Cancel" : "+ Upload Video"}
          </button>
        </div>

        {/* Upload form */}
        {showForm && (
          <form onSubmit={handleAdd} style={{
            background: "var(--sp-white)", border: "1px solid var(--sp-border)",
            borderRadius: "var(--radius-lg)", padding: "28px 32px", marginBottom: 28,
            animation: "scaleIn 0.25s ease"
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Upload New Video</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <FormField label="Title" value={form.title} onChange={v => setForm(p => ({...p, title: v}))} required />
              <FormField label="Instructor" value={form.instructor} onChange={v => setForm(p => ({...p, instructor: v}))} required />
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--sp-gray)", marginBottom: 6 }}>Category</label>
                <select
                  value={form.category}
                  onChange={e => setForm(p => ({...p, category: e.target.value}))}
                  style={{ ...adminInputStyle, width: "100%" }}
                >
                  {CATEGORIES.filter(c => c !== "All").map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <FormField label="YouTube Video ID" value={form.youtubeId} onChange={v => setForm(p => ({...p, youtubeId: v}))} placeholder="e.g. dQw4w9WgXcQ" required />
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--sp-gray)", marginBottom: 6 }}>Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({...p, description: e.target.value}))}
                rows={3}
                style={{ ...adminInputStyle, width: "100%", resize: "vertical" }}
                placeholder="Brief description of the lecture…"
              />
            </div>
            {form.youtubeId && (
              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: 12, color: "var(--sp-gray-mid)", marginBottom: 8 }}>Thumbnail preview:</p>
                <img
                  src={`https://img.youtube.com/vi/${form.youtubeId}/hqdefault.jpg`}
                  alt="Thumbnail preview"
                  style={{ height: 100, borderRadius: 6, objectFit: "cover" }}
                  onError={e => { e.target.style.display = "none"; }}
                />
              </div>
            )}
            <button type="submit" style={{
              marginTop: 20, padding: "12px 24px",
              background: "var(--sp-black)", color: "var(--sp-yellow)",
              fontWeight: 700, fontSize: 14, border: "none",
              borderRadius: "var(--radius-md)", cursor: "pointer",
              fontFamily: "var(--font-body)"
            }}>
              Add to Library →
            </button>
          </form>
        )}

        {/* Video table */}
        <div style={{
          background: "var(--sp-white)", border: "1px solid var(--sp-border)",
          borderRadius: "var(--radius-lg)", overflow: "hidden"
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--sp-black)" }}>
                {["Video", "Category", "Instructor", "Views", "Date", "Actions"].map(h => (
                  <th key={h} style={{
                    padding: "12px 16px", textAlign: "left",
                    fontSize: 11, fontWeight: 700, color: "var(--sp-yellow)",
                    letterSpacing: "0.08em", textTransform: "uppercase"
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {videos.map((v, i) => (
                <tr key={v.id} style={{
                  borderBottom: "1px solid var(--sp-border)",
                  transition: "background 0.15s ease",
                  animation: `fadeIn 0.3s ease ${i * 0.04}s both`
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#FFFCE6"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <img
                        src={v.thumbnail}
                        alt=""
                        style={{ width: 60, height: 36, objectFit: "cover", borderRadius: 4 }}
                        onError={e => { e.target.style.display = "none"; }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                        {v.title}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "var(--sp-black)",
                      background: "var(--sp-yellow-light)", border: "1px solid rgba(255,214,0,0.4)",
                      padding: "3px 10px", borderRadius: 99, whiteSpace: "nowrap"
                    }}>{v.category}</span>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--sp-gray-mid)" }}>{v.instructor}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13 }}>{Number(v.views).toLocaleString()}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "var(--sp-gray-mid)" }}>{v.uploadedAt}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {deleteId === v.id ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => handleDelete(v.id)} style={dangerBtnStyle}>Confirm</button>
                        <button onClick={() => setDeleteId(null)} style={ghostBtnStyle}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteId(v.id)} style={ghostBtnStyle}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StudentList() {
  const [students, setStudents] = useState(MOCK_STUDENTS);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = students.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    s.courseLevel.toLowerCase().includes(search.toLowerCase())
  );

  function toggleStatus(id) {
    setStudents(prev => prev.map(s =>
      s.id === id ? { ...s, status: s.status === "active" ? "suspended" : "active" } : s
    ));
    if (selected?.id === id) {
      setSelected(prev => ({ ...prev, status: prev.status === "active" ? "suspended" : "active" }));
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--sp-surface)", padding: "32px 24px", fontFamily: "var(--font-body)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 36, letterSpacing: "0.04em", marginBottom: 4 }}>
          STUDENT ACCOUNTS
        </h1>
        <p style={{ color: "var(--sp-gray-mid)", marginBottom: 24 }}>{students.length} registered students</p>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, course level…"
          style={{ ...adminInputStyle, width: "100%", marginBottom: 20, padding: "12px 16px" }}
        />

        <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 340px" : "1fr", gap: 20 }}>
          {/* Table */}
          <div style={{
            background: "var(--sp-white)", border: "1px solid var(--sp-border)",
            borderRadius: "var(--radius-lg)", overflow: "hidden"
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--sp-black)" }}>
                  {["Student", "Course", "Status", "Videos Watched", "Joined", ""].map(h => (
                    <th key={h} style={{
                      padding: "12px 16px", textAlign: "left",
                      fontSize: 11, fontWeight: 700, color: "var(--sp-yellow)",
                      letterSpacing: "0.08em", textTransform: "uppercase"
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelected(selected?.id === s.id ? null : s)}
                    style={{
                      borderBottom: "1px solid var(--sp-border)",
                      cursor: "pointer",
                      background: selected?.id === s.id ? "#FFFCE6" : "transparent",
                      transition: "background 0.15s",
                      animation: `fadeIn 0.3s ease ${i * 0.04}s both`
                    }}
                    onMouseEnter={e => { if (selected?.id !== s.id) e.currentTarget.style.background = "#fafafa"; }}
                    onMouseLeave={e => { if (selected?.id !== s.id) e.currentTarget.style.background = "transparent"; }}
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: s.status === "active" ? "var(--sp-yellow-light)" : "#f3f4f6",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700, color: "var(--sp-black)", flexShrink: 0
                        }}>
                          {s.name[0]}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: "var(--sp-gray-mid)" }}>{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "var(--sp-gray-mid)" }}>{s.courseLevel}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
                        background: s.status === "active" ? "var(--sp-green-light)" : "var(--sp-red-light)",
                        color: s.status === "active" ? "var(--sp-green)" : "var(--sp-red)"
                      }}>
                        {s.status === "active" ? "Active" : "Suspended"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13 }}>{s.watchedVideos.length}</td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "var(--sp-gray-mid)" }}>{s.joinedAt}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <button
                        onClick={e => { e.stopPropagation(); toggleStatus(s.id); }}
                        style={{
                          padding: "5px 12px", fontSize: 11, fontWeight: 700,
                          background: "transparent",
                          color: s.status === "active" ? "var(--sp-red)" : "var(--sp-green)",
                          border: `1px solid ${s.status === "active" ? "rgba(220,38,38,0.3)" : "rgba(22,163,74,0.3)"}`,
                          borderRadius: 6, cursor: "pointer", fontFamily: "var(--font-body)"
                        }}
                      >
                        {s.status === "active" ? "Suspend" : "Reactivate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{
              background: "var(--sp-white)", border: "1px solid var(--sp-border)",
              borderRadius: "var(--radius-lg)", padding: "24px",
              animation: "slideInRight 0.25s ease",
              alignSelf: "start"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700 }}>Student Profile</h3>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--sp-gray-mid)", fontSize: 18 }}>✕</button>
              </div>

              <div style={{
                display: "flex", alignItems: "center", gap: 12, marginBottom: 20,
                padding: "16px", background: "var(--sp-surface)", borderRadius: "var(--radius-md)"
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "var(--sp-yellow)", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 20, fontWeight: 700
                }}>{selected.name[0]}</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: "var(--sp-gray-mid)" }}>{selected.email}</div>
                </div>
              </div>

              {[
                ["Phone", selected.phone],
                ["Course Level", selected.courseLevel],
                ["Joined", selected.joinedAt],
                ["Videos Watched", selected.watchedVideos.length]
              ].map(([label, value]) => (
                <div key={label} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "10px 0", borderBottom: "1px solid var(--sp-border)",
                  fontSize: 13
                }}>
                  <span style={{ color: "var(--sp-gray-mid)" }}>{label}</span>
                  <span style={{ fontWeight: 600 }}>{value}</span>
                </div>
              ))}

              <button
                onClick={() => toggleStatus(selected.id)}
                style={{
                  width: "100%", marginTop: 20, padding: "11px",
                  background: selected.status === "active" ? "var(--sp-red-light)" : "var(--sp-green-light)",
                  color: selected.status === "active" ? "var(--sp-red)" : "var(--sp-green)",
                  border: `1px solid ${selected.status === "active" ? "rgba(220,38,38,0.3)" : "rgba(22,163,74,0.3)"}`,
                  borderRadius: "var(--radius-md)", fontWeight: 700, fontSize: 13,
                  cursor: "pointer", fontFamily: "var(--font-body)", transition: "all 0.15s"
                }}
              >
                {selected.status === "active" ? "Suspend Account" : "Reactivate Account"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared styles
const adminInputStyle = {
  padding: "10px 12px", fontSize: 14,
  background: "var(--sp-surface)", border: "1px solid var(--sp-border)",
  borderRadius: "var(--radius-sm)", outline: "none",
  fontFamily: "var(--font-body)", color: "var(--sp-black)",
  transition: "border-color 0.2s"
};

const ghostBtnStyle = {
  padding: "5px 12px", fontSize: 11, fontWeight: 600,
  background: "transparent", border: "1px solid var(--sp-border)",
  borderRadius: 6, cursor: "pointer", color: "var(--sp-gray-mid)",
  fontFamily: "var(--font-body)"
};

const dangerBtnStyle = {
  ...ghostBtnStyle,
  color: "var(--sp-red)", borderColor: "rgba(220,38,38,0.3)"
};

function FormField({ label, value, onChange, placeholder, required }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--sp-gray)", marginBottom: 6 }}>
        {label}
      </label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ""}
        required={required}
        style={{ ...adminInputStyle, width: "100%" }}
      />
    </div>
  );
}
